import {formatDate} from '@angular/common';
import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {Router, RouterLink} from '@angular/router';
import {DayInformation} from 'src/app/shared/models/day-information.model';
import {SignUtils} from '../../core/utils/sign.utils';
import {DateAdapter} from "@angular/material/core";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {ElementComponent} from "../details/element/element.component";
import {MatDialog} from "@angular/material/dialog";
import {GardenComponent} from "../details/garden/garden.component";
import {NutritionQualityComponent} from "../details/nutrition-quality/nutrition-quality.component";
import {DayQualityComponent} from "../details/day-quality/day-quality.component";
import {MoonComponent} from "../details/moon/moon.component";
import {SignComponent} from "../details/sign/sign.component";
import {MatCard, MatCardContent, MatCardTitle} from "@angular/material/card";
import {DateShortPipe, TimeShortPipe} from "../../core/utils/pipe.utils";
import {MatFormField, MatLabel, MatSuffix} from '@angular/material/form-field';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {FormsModule} from "@angular/forms";
import {MatInput} from "@angular/material/input";
import {routeDateFormat} from "../../core/constants/route.formats";
import {LanguageService} from '../../services/language.service';
import {getSiteDateKey} from '../../core/utils/date.utils';

@Component({
    selector: 'app-information',
    templateUrl: './information.component.html',
    imports: [
        MatCard,
        MatCardContent,
        TranslatePipe,
        TimeShortPipe,
        MatFormField,
        MatInput,
        MatLabel,
        MatSuffix,
        MatDatepickerToggle,
        MatDatepicker,
        MatDatepickerInput,
        RouterLink,
        DateShortPipe,
        FormsModule,
        MatCardTitle
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./information.component.scss']
})
export class InformationComponent implements OnInit {

    private _dayInformation: DayInformation | undefined;

    @Input()
    set dayInformation(dayInformation: DayInformation | undefined) {
        this._dayInformation = dayInformation;
        this.relativeDay = this.getRelativeDay(dayInformation);
    }

    get dayInformation(): DayInformation | undefined {
        return this._dayInformation;
    }

    relativeDay = '';

    // Utils
    signUtils = new SignUtils();

    constructor(
        private router: Router,
        private dateAdapter: DateAdapter<any>,
        private translate: TranslateService,
        private dialog: MatDialog,
        public language: LanguageService
    ) {
    }

    ngOnInit(): void {
        this.setDateAdapterLocale()
    }

    switchDay(event: any) {
        const newDay = new Date();
        newDay.setDate(event.value.getDate());
        const newDayString = formatDate(newDay, routeDateFormat, 'en');
        this.router.navigateByUrl(this.language.route(`/day/${newDayString}`));
    }

    getSwitchDate(addDay: number): string {
        if (!this.dayInformation) {
            return "";
        }

        const previous = new Date(this.dayInformation.day);
        previous.setDate(previous.getDate() + addDay);

        return formatDate(previous, routeDateFormat, 'en');
    }

    getElement(): string {
        if (!this.dayInformation) {
            return "";
        }

        return this.signUtils.getElementForSign(this.dayInformation.sign.id);
    }

    getGarden(): string {
        if (!this.dayInformation) {
            return "";
        }

        return this.signUtils.getGardenForSign(this.dayInformation.sign.id);
    }

    getOrganSystem(): string {
        if (!this.dayInformation) {
            return "";
        }

        return this.signUtils.getOrganSystemForSign(this.dayInformation.sign.id);
    }

    getDirection(): string {
        if (!this.dayInformation) {
            return "";
        }

        return this.signUtils.getDirectionForSign(this.dayInformation.sign.id);
    }

    getNutritionQuality(): string {
        if (!this.dayInformation) {
            return "";
        }

        return this.signUtils.getNutritionQualityForSign(this.dayInformation.sign.id);
    }

    getDayQuality(): string {
        if (!this.dayInformation) {
            return "";
        }

        return this.signUtils.getDayQualityForSign(this.dayInformation.sign.id);
    }

    openElementDetails() {
        const elementId = this.getElement()

        this.dialog.open(ElementComponent, {
            data: elementId,
            width: 'min(720px, 92vw)',
            maxWidth: '92vw'
        });
    }

    openGardenDetails() {
        const gardenId = this.getGarden()

        this.dialog.open(GardenComponent, {
            data: gardenId,
            width: 'min(720px, 92vw)',
            maxWidth: '92vw'
        });
    }

    openNutritionDetails() {
        const nutritionId = this.getNutritionQuality()

        this.dialog.open(NutritionQualityComponent, {
            data: nutritionId,
            width: 'min(720px, 92vw)',
            maxWidth: '92vw'
        });
    }

    openDayQualityDetails() {
        const dayQualityId = this.getDayQuality()

        this.dialog.open(DayQualityComponent, {
            data: dayQualityId,
            width: 'min(720px, 92vw)',
            maxWidth: '92vw'
        });
    }

    openMoonDetails() {
        if (!this.dayInformation) {
            return;
        }

        const phase = this.dayInformation.phase

        this.dialog.open(MoonComponent, {
            data: phase,
            width: 'min(720px, 92vw)',
            maxWidth: '92vw'
        });
    }

    openSignDetails() {
        if (!this.dayInformation) {
            return;
        }

        const signId = this.dayInformation.sign.id

        this.dialog.open(SignComponent, {
            data: signId,
            width: 'min(760px, 92vw)',
            maxWidth: '92vw'
        });
    }

    private setDateAdapterLocale() {
        this.dateAdapter.setLocale(this.translate.currentLang);
    }

    private getRelativeDay(dayInformation: DayInformation | undefined): string {
        if (!dayInformation) {
            return '';
        }

        const now = new Date();
        const dayKey = getSiteDateKey(dayInformation.day);

        if (dayKey === getSiteDateKey(now, -1)) {
            return 'yesterday';
        }

        if (dayKey === getSiteDateKey(now)) {
            return 'today';
        }

        if (dayKey === getSiteDateKey(now, 1)) {
            return 'tomorrow';
        }

        return '';
    }
}
